/* eslint-disable camelcase, max-len, object-curly-newline  */
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore - Got issue: An unexpected error occurred:
// "https://registry.yarnpkg.com/@types%2freact-id-swiper: Not found".
import Swiper from 'react-id-swiper';

import { AgentRoleCard } from '../../postjob/rolecard/agentview/AgentRoleCard';
import AgentTimeDateCard from '../../postjob/rolecard/agentview/agenttimedatecard';
import { AgentLocationCard } from '../../postjob/rolecard/agentview/AgentLocationCard';
import { AgentDelegateCard, TAdditionalService } from '../../postjob/rolecard/agentview/AgentDelegateCard';
import { setRoleExtra, updateField, updateRoleExtra } from '../../../actions/BookingRequest';
import {
    fetchClientData,
    clearPaymentMethods,
} from '../../../actions/ClientData';
import { ErrorMessage } from '../../common';
import { jobModes, serviceTypes } from '../../../consts';
import { serviceSpecificFields, TDbsCheck, TMatchingMode, TServiceKey } from '../../../types';
import history from '../../../history';
import { TRootState } from '../../../reducers';
import { AgentMultiServiceCard } from '../../postjob/rolecard/agentview/AgentMultiServiceCard';
import { reduceObject } from '../../../utils';
import { usePrevious } from '../../../hooks';

export type TJobRequestExtraRate = {
    extra_rate: number;
    freelancer_pay_per_hour?: number;
    client_pay_per_hour?: number;
    description?: string;
}

type TInitialBookingRequest = {
    service: TServiceKey;
    additional_services: TAdditionalService[];
    date: string;
    dates: string[];
    rangeDatesSelected: string[];
    isMultiple: boolean;
    isRemoving: boolean;
    start_time: string;
    duration: number;
    recurrence_period: number;
    number_of_freelancers: number;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    admin_information: string;
    freelancers_information: string;
    nature_of_business: string;
    required_qualifications: string;
    safety_risks: string;
    available_facilities: string;
    expenses_payable: string;
    title: string;
    dynamic_opt_in: number;
    voucher: string;
    matching_mode: TMatchingMode;
    breaks: number;
    rates: TJobRequestExtraRate[];
    dbs_check: TDbsCheck;
    consultant_splits: TConsultantSplit[];
    qualifying_questions: TQualifyingQuestions[];
};

const initialBookingRequest: TInitialBookingRequest = {
    service: null,
    additional_services: [],
    date: null,
    dates: [],
    rangeDatesSelected: [],
    isMultiple: false,
    isRemoving: false,
    start_time: null,
    duration: 4,
    recurrence_period: 0,
    number_of_freelancers: 1,
    address1: null,
    address2: null,
    city: null,
    postcode: null,
    admin_information: null,
    freelancers_information: null,
    nature_of_business: null,
    required_qualifications: null,
    safety_risks: null,
    available_facilities: null,
    expenses_payable: null,
    title: null,
    dynamic_opt_in: 0,
    voucher: null,
    matching_mode: 'MM',
    breaks: null,
    rates: [],
    dbs_check: 'NO',
    consultant_splits: [],
    qualifying_questions: [],
};

export type TConsultantSplit = {
    agent: number;
    split_value: number | string;
};

export type TQualifyingQuestions = {
    content: string;
    correct_answer: boolean;
};

export type TJob = {
    date: string;
    duration: number;
    service: TServiceKey;
    additional_services: TAdditionalService[];
    number_of_freelancers: number;
    start_time: string;
    freelancer_pay_per_hour: number;
    client_pay_per_hour: number;
    address1: string;
    address2: string;
    postcode: string;
    city: string;
    payment_method: PaymentMethodData,
    matching_mode: TMatchingMode;
    breaks: number;
    client: number;
    freelancers_information: string;
    admin_information: string;
    nature_of_business: string;
    required_qualifications: string;
    safety_risks: string;
    available_facilities: string;
    expenses_payable: string;
    title: string;
    vehicle_type?: string;
    client_full: {
        id: number;
        reference_number: string;
        first_name: string;
        last_name: string;
        company_name: string;
        mobile: string;
        logo: string;
        legal_name: string;
        default_admin_information: string;
        average_score: number;
        date_joined: string;
        default_freelancer_information: string;
        signup_medium: string;
        default_consultant: number;
    };
    managed: boolean;
    owned: boolean;
    rates: TJobRequestExtraRate[];
    dbs_check: TDbsCheck;
    consultant_splits: TConsultantSplit[];
    qualifying_questions: TQualifyingQuestions[];
    series: number;
};

export type TRoleExtras = {
    agency?: number;
    minimum_delivery_box?: string;
    medium_van?: number;
    large_van?: number;
    vehicle_type?: string;
    own_vehicle?: number;
    role?: string;
    supervisor?: string;
    cocktail_making?: string;
    food_preparation?: string;
    heavy_lifting?: string;
    sous_chef?: string;
    commis_chef?: string;
    chef_de_partie?: string;
    pastry_chef?: string;
};

type TAgentShiftForm = {
    defaultJob: TJob;
    handleFormSubmit: (formData: any) => void;
    mode: string;
    isLoading: boolean;
    errorMessage: string;
    setErrorMessage: (message: string) => void;
    roleExtras?: TRoleExtras;
    initialService?: TServiceKey,
};

export function AgentShiftForm({
    defaultJob,
    handleFormSubmit,
    mode,
    isLoading,
    errorMessage,
    setErrorMessage,
    roleExtras,
    initialService,
}: TAgentShiftForm) {
    const dispatch = useDispatch();
    const [copyBookings, setCopyBookings] = useState(false);

    const [isMultiService, setIsMultiService] = useState(false);
    const [selectedServices, setSelectedServices] = useState<TServiceKey[]>([]);

    const [defaultNature, setDefaultNature] = useState('');
    const [defaultQualifications, setDefaultQualifications] = useState('');
    const [defaultRisks, setDefaultRisks] = useState('');
    const [defaultFacilities, setDefaultFacilities] = useState('');
    const [defaultPayable, setDefaultPayable] = useState('');

    const handleSetSelectedServices = (services: TServiceKey[]) => {
        const sortingArr = Object.values(serviceTypes);
        setSelectedServices(services.sort((a, b) =>
            sortingArr.indexOf(a) - sortingArr.indexOf(b))
        );
    };

    const changeCopyBookings = (value: boolean) => setCopyBookings(value);

    const {
        agentsLoadingError,
        basicInfo,
        defaultAgencyAssignmentDetails,
    } = useSelector((state: TRootState) => ({
        agentsLoadingError: state.agents.error,
        basicInfo: state.clientData.basicInfo,
        defaultAgencyAssignmentDetails: state.user.agency.default_assignment_details,
    }));

    const prevBasicInfo = usePrevious(basicInfo);

    const agentsErrorMessage = agentsLoadingError !== undefined
        ? 'Sorry, we couldn\'t load Consultants. Please try again later.'
        : '';

    const setDates = useCallback(() => {
        if (mode === jobModes.EDIT) {
            Object.keys(initialBookingRequest).forEach(key => {
                dispatch(updateField(key, initialBookingRequest[key as keyof TInitialBookingRequest]));
            });
        } else {
            dispatch(updateField('date', initialBookingRequest.date));
            dispatch(updateField('dates', initialBookingRequest.dates));
        }

        if (mode === jobModes.CREATE) {
            dispatch(updateField('qualifying_questions', initialBookingRequest.qualifying_questions));
            dispatch(updateField('rates', initialBookingRequest.rates));
        }

        dispatch(updateField('isMultiple', initialBookingRequest.isMultiple));
        dispatch(updateField('rangeDatesSelected', initialBookingRequest.rangeDatesSelected));
        dispatch(updateField('date', defaultJob.date));
        dispatch(updateField('start_time', defaultJob.start_time));
        dispatch(updateField('breaks', initialBookingRequest.breaks));
    }, [defaultJob.date, defaultJob.start_time, dispatch, mode]);

    const setServiceType = useCallback(() => {
        const service = defaultJob.service || 'driver';
        const currentUrlParams = new URLSearchParams(history.location.search);
        const role = history && currentUrlParams.get('role');

        if (mode === jobModes.CREATE) {
            if (service === 'driver' || !role) {
                dispatch(updateField('service', service));
                dispatch(updateRoleExtra('driver', 'vehicle_type', defaultJob.vehicle_type));
            } else {
                dispatch(updateField('service', service));
                dispatch(updateRoleExtra(service, 'role', role));
            }
        }

        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            dispatch(updateField('service', service));

            Object.keys(roleExtras).forEach(key => {
                if (
                    typeof roleExtras[key as keyof TRoleExtras] !== 'undefined' ||
                    roleExtras[key as keyof TRoleExtras] !== 'KP'
                ) {
                    dispatch(updateRoleExtra(service, key, roleExtras[key as keyof TRoleExtras]));
                }
            });

            Object.keys(defaultJob).forEach(key => {
                dispatch(updateField(key, defaultJob[key as keyof TJob]));
            });

            const client = defaultJob.client_full;
            dispatch(fetchClientData(client));
        }
    }, [defaultJob, dispatch, mode, roleExtras]);

    const setConsultantSplits = useCallback(() => {
        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            const consultantSplits = defaultJob.consultant_splits;
            dispatch(updateField('consultant_splits', consultantSplits));
        }
    }, [defaultJob, dispatch, mode]);

    const setQualifyingQuestions = useCallback(() => {
        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            const qualifyingQuestions: TQualifyingQuestions[] = defaultJob.qualifying_questions;
            dispatch(updateField('qualifying_questions', qualifyingQuestions));
        }
    }, [defaultJob.qualifying_questions, dispatch, mode]);

    const setAdditionalServices = useCallback(() => {
        if (mode === jobModes.CREATE) {
            dispatch(updateField('additional_services', initialBookingRequest.additional_services));
        } else if ((mode === jobModes.EDIT || mode === jobModes.DUPLICATE) && defaultJob.additional_services?.length) {
            const services: TServiceKey[] = [defaultJob.service];
            defaultJob.additional_services.forEach((additionalService: TAdditionalService) => {
                services.push(additionalService.service);

                const keys = serviceSpecificFields[additionalService.service];
                const roleExtra = reduceObject(additionalService, keys);

                dispatch(setRoleExtra(additionalService.service, roleExtra));
            });
            handleSetSelectedServices(services);
            setIsMultiService(!!defaultJob.additional_services?.length);
            dispatch(updateField('additional_services', defaultJob.additional_services));
        }
    }, [defaultJob.additional_services, defaultJob.service, dispatch]);

    useEffect(() => {
        setDates();
        setServiceType();
        setConsultantSplits();
        setQualifyingQuestions();
        setAdditionalServices();

        return () => {
            dispatch(clearPaymentMethods());
        };
    }, []);

    useEffect(() => {
        if (mode === jobModes.CREATE && basicInfo.id && basicInfo.id !== prevBasicInfo?.id) {
            setDefaultNature(basicInfo.default_nature_of_business || defaultAgencyAssignmentDetails.nature_of_business);
            setDefaultQualifications(basicInfo.default_required_qualifications || defaultAgencyAssignmentDetails.required_qualifications);
            setDefaultRisks(basicInfo.default_safety_risks || defaultAgencyAssignmentDetails.safety_risks);
            setDefaultFacilities(basicInfo.default_available_facilities || defaultAgencyAssignmentDetails.available_facilities);
            setDefaultPayable(basicInfo.default_expenses_payable || defaultAgencyAssignmentDetails.expenses_payable);
        }
    }, [basicInfo.id]);

    const swiperParams = {
        nextButton: '.swiper-button-next',
        prevButton: '.swiper-button-prev',
        spaceBetween: 20,
        containerClass: 'cards-container',
        rebuildOnUpdate: true,
        runCallbacksOnInit: true,
        observeParents: true,
    };

    const natureRef = useRef(null);
    const qualificationsRef = useRef(null);
    const risksRef = useRef(null);
    const facilitiesRef = useRef(null);
    const payableRef = useRef(null);
    const titleRef = useRef(null);
    const natureLabelRef = useRef(null);
    const qualificationsLabelRef = useRef(null);
    const risksLabelRef = useRef(null);
    const facilitiesLabelRef = useRef(null);
    const payableLabelRef = useRef(null);
    const titleLabelRef = useRef(null);

    return (
        <div>
            <div className="agent-shift-container visible-xs-flex visible-sm-flex visible-md-flex">
                <Swiper {...swiperParams}>
                    <AgentRoleCard
                        className="agent-role-card swiper-slide"
                        mode={mode}
                        isMultiService={isMultiService}
                        setIsMultiService={setIsMultiService}
                        selectedServices={selectedServices}
                        handleSetSelectedServices={handleSetSelectedServices}
                        setErrorMessage={setErrorMessage}
                    />
                    {isMultiService ? (
                        <AgentMultiServiceCard
                            className="agent-multiservice-card swiper-slide"
                            selectedServices={selectedServices}
                            handleSetSelectedServices={handleSetSelectedServices}
                            mode={mode}
                        />
                    ) : (
                        <></> // a ghost element for Swiper
                    )}
                    <AgentTimeDateCard
                        className="agent-time-card swiper-slide"
                        queryDate={defaultJob.date}
                        mode={mode}
                        copyBookings={copyBookings}
                        changeCopyBookings={changeCopyBookings}
                    />
                    <AgentLocationCard
                        mode={mode}
                        className="swiper-slide"
                        defaultNature={defaultNature}
                        defaultQualifications={defaultQualifications}
                        defaultRisks={defaultRisks}
                        defaultFacilities={defaultFacilities}
                        defaultPayable={defaultPayable}
                    />
                    <AgentDelegateCard
                        className="swiper-slide"
                        handleFormSubmit={handleFormSubmit}
                        mode={mode}
                        isLoading={isLoading}
                        natureRef={natureRef}
                        qualificationsRef={qualificationsRef}
                        risksRef={risksRef}
                        facilitiesRef={facilitiesRef}
                        payableRef={payableRef}
                        titleRef={titleRef}
                        natureLabelRef={natureLabelRef}
                        qualificationsLabelRef={qualificationsLabelRef}
                        risksLabelRef={risksLabelRef}
                        facilitiesLabelRef={facilitiesLabelRef}
                        payableLabelRef={payableLabelRef}
                        titleLabelRef={titleLabelRef}
                        initialExtraRates={defaultJob.rates}
                        copyBookings={copyBookings}
                        isMultiService={isMultiService}
                        selectedServices={selectedServices}
                        initialService={initialService}
                    />
                </Swiper>
                {errorMessage && <ErrorMessage message={errorMessage} />}
                {agentsErrorMessage && (
                    <ErrorMessage message={agentsErrorMessage} />
                )}
            </div>
            <div className="agent-shift-container hidden-xs hidden-sm hidden-md">
                <AgentRoleCard
                    className="agent-role-card"
                    mode={mode}
                    isMultiService={isMultiService}
                    setIsMultiService={setIsMultiService}
                    selectedServices={selectedServices}
                    handleSetSelectedServices={handleSetSelectedServices}
                    setErrorMessage={setErrorMessage}
                />
                {isMultiService && (
                    <AgentMultiServiceCard
                        className="agent-multiservice-card"
                        selectedServices={selectedServices}
                        handleSetSelectedServices={handleSetSelectedServices}
                        mode={mode}
                    />
                )}
                <AgentTimeDateCard
                    className="agent-time-card"
                    queryDate={defaultJob.date}
                    mode={mode}
                    copyBookings={copyBookings}
                    changeCopyBookings={changeCopyBookings}
                />
                <AgentLocationCard
                    mode={mode}
                    natureRef={natureRef}
                    qualificationsRef={qualificationsRef}
                    risksRef={risksRef}
                    facilitiesRef={facilitiesRef}
                    payableRef={payableRef}
                    titleRef={titleRef}
                    natureLabelRef={natureLabelRef}
                    qualificationsLabelRef={qualificationsLabelRef}
                    risksLabelRef={risksLabelRef}
                    facilitiesLabelRef={facilitiesLabelRef}
                    payableLabelRef={payableLabelRef}
                    titleLabelRef={titleLabelRef}
                    defaultNature={defaultNature}
                    defaultQualifications={defaultQualifications}
                    defaultRisks={defaultRisks}
                    defaultFacilities={defaultFacilities}
                    defaultPayable={defaultPayable}
                />
                <AgentDelegateCard
                    handleFormSubmit={handleFormSubmit}
                    mode={mode}
                    isLoading={isLoading}
                    natureRef={natureRef}
                    qualificationsRef={qualificationsRef}
                    risksRef={risksRef}
                    facilitiesRef={facilitiesRef}
                    payableRef={payableRef}
                    titleRef={titleRef}
                    natureLabelRef={natureLabelRef}
                    qualificationsLabelRef={qualificationsLabelRef}
                    risksLabelRef={risksLabelRef}
                    facilitiesLabelRef={facilitiesLabelRef}
                    payableLabelRef={payableLabelRef}
                    titleLabelRef={titleLabelRef}
                    initialExtraRates={defaultJob.rates}
                    copyBookings={copyBookings}
                    isMultiService={isMultiService}
                    selectedServices={selectedServices}
                    initialService={initialService}
                />
                {errorMessage && <ErrorMessage message={errorMessage} />}
                {agentsErrorMessage && (
                    <ErrorMessage message={agentsErrorMessage} />
                )}
            </div>
        </div>
    );
}
